/**
 * Key Backup Step - Second step of the setup wizard
 *
 * Shows backup options (PDF download) and confirmation.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Paper,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { StoredKeyPair, KeyOwnerType, useKeyBackup } from '@antragsmanager/crypto-lib';

interface KeyBackupStepProps {
  /** The key pair to backup */
  keyPair: StoredKeyPair;

  /** Owner name for the PDF */
  ownerName: string;

  /** Owner type for the PDF */
  ownerType: KeyOwnerType;

  /** Callback when backup is confirmed */
  onBackupConfirmed: () => void;

  /** Callback when user wants to go back */
  onBack?: () => void;
}

export function KeyBackupStep({
  keyPair,
  ownerName,
  ownerType,
  onBackupConfirmed,
  onBack,
}: KeyBackupStepProps) {
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const { isGenerating, downloadPdfForKey, generateQrCodeForKey } = useKeyBackup();

  // Generate QR code preview
  useEffect(() => {
    generateQrCodeForKey(keyPair)
      .then(setQrCodeDataUrl)
      .catch(console.error);
  }, [keyPair, generateQrCodeForKey]);

  // Handle PDF download
  const handleDownload = async () => {
    try {
      setDownloadError(null);
      await downloadPdfForKey(keyPair, {
        ownerName,
        ownerType,
      });
      setHasDownloaded(true);
    } catch (error) {
      setDownloadError(
        error instanceof Error
          ? error.message
          : 'Fehler beim Erstellen des PDFs.'
      );
    }
  };

  // Handle continue
  const handleContinue = () => {
    if (hasDownloaded && hasConfirmed) {
      onBackupConfirmed();
    }
  };

  const canContinue = hasDownloaded && hasConfirmed;

  return (
    <Box sx={{ py: 2 }}>
      {/* Warning header */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Wichtig:</strong> Laden Sie das Backup-PDF herunter und drucken Sie es aus.
          Ohne dieses Dokument können Sie sich nicht erneut anmelden!
        </Typography>
      </Alert>

      {/* QR Code preview */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          textAlign: 'center',
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Ihr persönlicher QR-Code
        </Typography>

        {qrCodeDataUrl ? (
          <Box
            component="img"
            src={qrCodeDataUrl}
            alt="Schlüssel QR-Code"
            sx={{
              width: 180,
              height: 180,
              mx: 'auto',
              my: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'white', // QR codes need white background
            }}
          />
        ) : (
          <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={40} />
          </Box>
        )}

        <Typography variant="body2" color="text.secondary">
          Dieser QR-Code ist Ihr Zugangsschlüssel zum System.
        </Typography>
      </Paper>

      {/* Download button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownload}
          disabled={isGenerating}
          sx={{
            py: 1.5,
            bgcolor: hasDownloaded ? 'success.main' : 'primary.main',
            '&:hover': {
              bgcolor: hasDownloaded ? 'success.dark' : 'primary.dark',
            },
          }}
        >
          {hasDownloaded ? 'Erneut herunterladen' : 'PDF-Backup herunterladen'}
        </Button>

        {hasDownloaded && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PictureAsPdfIcon />
              <Typography variant="body2">
                Das PDF wurde heruntergeladen. Bitte drucken Sie es aus und bewahren Sie es sicher auf.
              </Typography>
            </Box>
          </Alert>
        )}

        {downloadError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {downloadError}
          </Alert>
        )}
      </Box>

      {/* Confirmation checkbox */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          border: '2px solid',
          borderColor: hasConfirmed ? 'success.main' : 'divider',
          borderRadius: 2,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={hasConfirmed}
              onChange={(e) => setHasConfirmed(e.target.checked)}
              disabled={!hasDownloaded}
            />
          }
          label={
            <Typography variant="body2">
              Ich habe das PDF heruntergeladen und verstehe, dass ich ohne dieses
              Dokument keinen Zugang mehr zum System habe.
            </Typography>
          }
        />
      </Paper>

      {/* Security reminder */}
      <Alert
        severity="warning"
        sx={{ mb: 3 }}
        icon={<WarningIcon />}
      >
        <Typography variant="subtitle2">
          Sicherheitshinweise
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          1. Drucken Sie das PDF aus und bewahren Sie es sicher auf (z.B. Safe)<br />
          2. Erstellen Sie keine digitalen Kopien<br />
          3. Geben Sie den QR-Code niemals an Dritte weiter
        </Typography>
      </Alert>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {onBack && (
          <Button variant="outlined" onClick={onBack}>
            Zurück
          </Button>
        )}
        <Button
          variant="contained"
          fullWidth
          onClick={handleContinue}
          disabled={!canContinue}
        >
          Weiter
        </Button>
      </Box>
    </Box>
  );
}
